import { Injectable, computed, signal } from '@angular/core';
import { LoadingState } from '../../shared/models/loading-state.model';

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private readonly defaultMessage = 'Loading...';
  private readonly _counter = signal(0);
  private readonly _message = signal(this.defaultMessage);

  readonly visible = computed(() => this._counter() > 0);

  readonly state = computed<LoadingState>(() => ({
    visible: this.visible(),
    message: this._message(),
  }));

  show(message?: string): void {
    if (message?.trim()) {
      this._message.set(message.trim());
    } else if (!this.visible()) {
      this._message.set(this.defaultMessage);
    }

    this._counter.update((v) => v + 1);
  }

  hide(): void {
    this._counter.update((v) => Math.max(0, v - 1));

    if (this._counter() === 0) {
      this._message.set(this.defaultMessage);
    }
  }

  setMessage(message: string): void {
    this._message.set(message?.trim() || this.defaultMessage);
  }

  reset(): void {
    this._counter.set(0);
    this._message.set(this.defaultMessage);
  }

  async run<T>(task: () => Promise<T>, message?: string): Promise<T> {
    this.show(message);
    try {
      return await task();
    } finally {
      this.hide();
    }
  }
}
